/**
 * frontend/src/pages/valuation/_OwnerReportCard.tsx
 *
 * VAL-PATH-P6b — step 6 "Get your report", owner only. Where the Snapshot
 * stands (recorded, then paid) and the one way to the full report: send the
 * value to a valuation signer — a status change only (D8: no email, no new
 * record), nothing re-entered. "Record and order my Snapshot" is the footer's
 * final action (U1), so it has one home and is not repeated here.
 *
 * Every string is from OWNER_APPROVALS.log (10-21 line 80, 10-22 line 95).
 */
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import type { ValuationEngagement } from '@/types/valuation'
import { SnapshotDownloadButton } from './_SnapshotDownload'

/** The reason an owner's record carries; nobody types it (approved 10-22 09:33). */
export const OWNER_ESTIMATE_REASON = "Owner's Estimated Value"
export const RECORD_AND_ORDER = 'Record and order my Snapshot'
export const SEND_TO_SIGNER = 'Send to a valuation signer'

export function OwnerReportCard({
  engagement,
  recorded,
  snapshotOnFile,
  saving,
  onSend,
}: {
  engagement: ValuationEngagement
  /** A live owner record exists and nothing has changed since. */
  recorded: boolean
  /** The live record is the owner's own final estimate (FIX_VAL-SNAPSHOT-BUTTON-1); a signer's version has no Snapshot. */
  snapshotOnFile: boolean
  saving: boolean
  onSend: () => void
}) {
  const withSigner = engagement.status === 'in_cpa_review'
  return (
    <Card>
      <CardHeader><CardTitle>Get your report</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        {snapshotOnFile && (
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            {engagement.snapshot_paid ? (
              <>
                <span className="text-sm text-gray-700">Ready</span>
                <div className="sm:w-56"><SnapshotDownloadButton engagementId={engagement.valuation_engagement_id} /></div>
              </>
            ) : (
              <span className="text-sm text-gray-700">Waiting for payment</span>
            )}
          </div>
        )}
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          {withSigner ? (
            <span className="text-sm text-gray-700">With the signer</span>
          ) : (
            <Button type="button" variant="outline" disabled={!recorded || saving} onClick={onSend}>
              {SEND_TO_SIGNER}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
